import { AvSubsystemAvServiceType } from "@minervaproject/avsubsystem-core";
import clsx from "clsx";
import { FC } from "react";

import { useAppSelector } from "../../store/index";
import { selectActiveClassSession } from "../../store/selectors/class-session";
import { selectCurrentCourseTitle, selectUserById } from "../../store/selectors/course";

import { TD } from "./Table";

import { ClassSessionCategory } from "components/server-types";
import {
  selectCurrentCourseId,
  selectOrgNameByCourseId,
  selectVirtualClassAvService,
  selectVirtualClassLicodeServerUrl,
} from "store/selectors";
import { buildDatadogFacetedUrl } from "utils/monitoring";
import { courseHomeUrl } from "utils/urls";
import { getUserFullName } from "utils/user-utils";

const CourseInfoHeader: FC = () => {
  const classSession = useAppSelector(selectActiveClassSession);
  const isVirtualClass = classSession?.category === ClassSessionCategory.VIRTUAL;
  const isPresenting = !!classSession?.presented_page_id;
  const avService = useAppSelector(selectVirtualClassAvService);
  return (
    <thead>
      <tr>
        <th className="!pl-0">Course ID</th>
        <th>Title</th>
        <th>Org</th>
        <th>Status</th>
        {isPresenting && <th>Presenting Page ID</th>}
        {isPresenting && <th>Presenting User</th>}
        {isVirtualClass && (
          <>
            <th>A/V Service</th>
            {avService === AvSubsystemAvServiceType.Licode && <th>Licode Server</th>}
          </>
        )}
        <th>Links</th>
      </tr>
    </thead>
  );
};

const CourseInfoRow: FC<{ courseId: string }> = ({ courseId }) => {
  const avService = useAppSelector(selectVirtualClassAvService) as AvSubsystemAvServiceType;
  const licodeServer = useAppSelector(selectVirtualClassLicodeServerUrl);
  const orgName = useAppSelector((state) => selectOrgNameByCourseId(state, courseId));
  const courseTitle = useAppSelector(selectCurrentCourseTitle);
  const classSession = useAppSelector(selectActiveClassSession);
  const isVirtualClass = classSession?.category === ClassSessionCategory.VIRTUAL;
  const isPresenting = !!classSession?.presented_page_id;
  const status = isPresenting ? "Presenting" : "Not Presenting";
  const presentingPageId = classSession?.presented_page_id;
  const presentingUser = useAppSelector((state) =>
    selectUserById(state, classSession?.presenting_user_id),
  );

  return (
    <tr>
      <TD dataTestId="course-id">{courseId}</TD>
      <TD dataTestId="course-title">{courseTitle}</TD>
      <TD dataTestId="org-name">{orgName}</TD>
      <TD className={clsx({ "text-green-shade-20": isPresenting })} dataTestId="status">
        {status}
      </TD>
      {isPresenting && <TD dataTestId="presenting-page-id">{presentingPageId}</TD>}
      {isPresenting && <TD dataTestId="presenting-user">{getUserFullName(presentingUser)}</TD>}
      {isVirtualClass && (
        <>
          <TD>{avService}</TD>
          {avService === AvSubsystemAvServiceType.Licode && <TD>{licodeServer}</TD>}
        </>
      )}
      <TD className="flex gap-2 text-lg" noWrap>
        <a href={courseHomeUrl(courseId)} rel="noreferrer" target="_blank">
          👀
        </a>
        <a href={buildDatadogFacetedUrl({ courseId })} rel="noreferrer" target="_blank">
          🐕
        </a>
      </TD>
    </tr>
  );
};

export const CourseInfo: FC = () => {
  const courseId = useAppSelector(selectCurrentCourseId);
  if (!courseId) return null;
  return (
    <table className="mds-table table-hover" data-testid="course-stats-table">
      <CourseInfoHeader />
      <tbody>
        <CourseInfoRow courseId={courseId} />
      </tbody>
    </table>
  );
};
